import { Button, TextArea } from "@blueprintjs/core";
import { useState } from "react";
import styled from "styled-components";
type Props = {
  onSend: (text: string) => void;
  loading: boolean;
};

export default function ChatInput({ onSend, loading }: Props) {
  const [text, setText] = useState("");

  const submit = () => {
    if (!text.trim()) return;

    onSend(text);
    setText("");
  };

  // Handle the Enter key press
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit only if Enter is pressed WITHOUT the Shift key
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); 
      submit();
    }
  };

  return (
    <InputWrapper>
      <StyledTextArea
        value={text}
        placeholder="Ask me anything..."
        onChange={(e) => setText(e.target.value)}
        disabled={false}
        fill
        onKeyDown={handleKeyDown}
        growVertically={true}
      />

      <StyledButton
        intent="primary"
        onClick={submit}
        icon="send-message"
        loading={loading}
        disabled={loading || !text.trim()}
        type="button"
      />
    </InputWrapper>
  );
}

const InputWrapper = styled.div`
  display: flex;
  align-items: end;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 28px;
  background: ${({ theme: { darkMode } }) =>
    darkMode ? "rgba(148, 163, 184, 0.12)" : "#f3f4f6"};
  border: 1px solid
    ${({ theme: { darkMode } }) =>
      darkMode ? "rgba(148, 163, 184, 0.24)" : "#d1d5db"};
  transition:
    border-color 150ms ease,
    box-shadow 150ms ease;

  &:focus-within {
    border-color: ${({ theme: { darkMode } }) =>
      darkMode ? "rgba(148, 163, 184, 0.75)" : "#9ca3af"};
    box-shadow: 0 0 0 3px
      ${({ theme: { darkMode } }) =>
        darkMode ? "rgba(148, 163, 184, 0.12)" : "rgba(156, 163, 175, 0.18)"};
  }
`;

const StyledTextArea = styled(TextArea)`
  flex: 1;
  min-height: 42px !important;
  max-height: 200px !important;
  border: none !important;
  background: transparent !important;
  color: ${({ theme: { darkMode } }) => (darkMode ? "#e5e7eb" : "#111827")};
  font-size: 0.95rem;
  line-height: 1.5;
  padding: 12px 0 !important;
  box-shadow: unset !important;
  overflow-y: auto;
  scrollbar-width: none;

  resize: none;
  .bp4-input {
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    outline: none !important;
  }

  .bp4-input:focus,
  .bp4-input:focus-visible,
  textarea:focus {
    border: none !important;
    box-shadow: none !important;
    outline: none !important;
  }

  textarea::-webkit-scrollbar {
    display: none;
  }
`;

const StyledButton = styled(Button)`
  min-width: 46px;
  width: 46px;
  height: 46px;
  border-radius: 999px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff !important;
  box-shadow: 0 8px 16px rgba(67, 56, 202, 0.18);
  border: none !important;
  margin-bottom: 7px;
  span {
    transform: rotate(-90deg);
  }
`;
